import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { EndUserDetailLink } from "@/components/inside-sales/EndUserDetailLink";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { hubModuleAllowsAdminSettings } from "@/lib/subapp-access";
import { useIntegratorMe } from "@/lib/me-query";
import {
  getCommissionRules,
  listCommissionEndUsers,
  patchCommissionEndUserProfile,
  commissionsEndUsersQueryKey,
  commissionsRulesQueryKey,
  commissionsStableListQueryOptions,
} from "./commissionsApi";

type EndUserRow = Awaited<ReturnType<typeof listCommissionEndUsers>>["rows"][number];

const FIRST_SOLD_COLUMN_HOVER_HINT =
  "Previously called true first sold. This is the actual date we first sold to them.";

const LAST_SOLD_COLUMN_HOVER_HINT =
  "Most recent sale date from sales orders (sales_order.submitted).";

type SortKey =
  | "name"
  | "new_end_user_bonus_starts_on"
  | "new_end_user_bonus_ends_on"
  | "first_sold"
  | "date_last_sold"
  | "commission_adjustment_rate";

function formatDate(value: string | null): string {
  if (value == null || value.trim() === "") {
    return "—";
  }
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatAdjustment(rate: number | null): string {
  if (rate == null) {
    return "—";
  }
  return `${(rate * 100).toFixed(2)}%`;
}

function parseAdjustmentPercentInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "—") {
    return null;
  }
  const normalized = trimmed.endsWith("%") ? trimmed.slice(0, -1).trim() : trimmed;
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed / 100;
}

function formatAdjustmentPercentInput(rate: number | null): string {
  if (rate == null) {
    return "";
  }
  return (rate * 100).toFixed(2).replace(/\.00$/, "");
}

function compareNullableStrings(left: string | null, right: string | null): number {
  const leftEmpty = left == null || left.trim() === "";
  const rightEmpty = right == null || right.trim() === "";
  if (leftEmpty && rightEmpty) {
    return 0;
  }
  if (leftEmpty) {
    return 1;
  }
  if (rightEmpty) {
    return -1;
  }
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

function compareNullableNumbers(left: number | null, right: number | null): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  return left - right;
}

function compareRows(
  left: EndUserRow,
  right: EndUserRow,
  sortKey: SortKey,
  sortDir: SortDirection,
): number {
  let cmp = 0;
  switch (sortKey) {
    case "name":
      cmp = compareNullableStrings(left.name, right.name);
      break;
    case "new_end_user_bonus_starts_on":
      cmp = compareNullableStrings(left.new_end_user_bonus_starts_on, right.new_end_user_bonus_starts_on);
      break;
    case "new_end_user_bonus_ends_on":
      cmp = compareNullableStrings(left.new_end_user_bonus_ends_on, right.new_end_user_bonus_ends_on);
      break;
    case "first_sold":
      cmp = compareNullableStrings(left.first_sold, right.first_sold);
      break;
    case "date_last_sold":
      cmp = compareNullableStrings(left.date_last_sold, right.date_last_sold);
      break;
    case "commission_adjustment_rate":
      cmp = compareNullableNumbers(left.commission_adjustment_rate, right.commission_adjustment_rate);
      break;
    default: {
      const _exhaustive: never = sortKey;
      return _exhaustive;
    }
  }
  if (cmp === 0) {
    cmp = left.fs_account_id - right.fs_account_id;
  }
  return sortDir === "asc" ? cmp : -cmp;
}

function CountLabel({ rows }: { rows: EndUserRow[] }): ReactNode {
  const withAdjustment = rows.filter((row) => row.commission_adjustment_rate != null).length;
  return (
    <span className="font-normal text-slate-600">
      ({String(rows.length)}
      {withAdjustment > 0 ? ` · ${String(withAdjustment)} with commission adjustment` : ""})
    </span>
  );
}

export function CommissionEndUsersPage() {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const canEdit = hubModuleAllowsAdminSettings(meQuery.data, "commissions");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [editRow, setEditRow] = useState<EndUserRow | null>(null);
  const [modalRateInput, setModalRateInput] = useState("");
  const [modalReason, setModalReason] = useState("");

  const rulesQuery = useQuery({
    queryKey: commissionsRulesQueryKey,
    queryFn: getCommissionRules,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });
  const newEndUserRule = rulesQuery.data?.rules.find((rule) => rule.id === "new_end_user");

  const listQuery = useQuery({
    queryKey: commissionsEndUsersQueryKey,
    queryFn: listCommissionEndUsers,
    ...commissionsStableListQueryOptions,
  });

  const saveMutation = useMutation({
    mutationFn: (input: {
      fsAccountId: number;
      commission_adjustment_rate: number | null;
      commission_adjustment_reason: string | null;
    }) =>
      patchCommissionEndUserProfile(input.fsAccountId, {
        commission_adjustment_rate: input.commission_adjustment_rate,
        commission_adjustment_reason: input.commission_adjustment_reason,
      }),
    onSuccess: () => {
      setEditRow(null);
      void queryClient.invalidateQueries({ queryKey: commissionsEndUsersQueryKey, exact: true });
    },
  });

  const rows = listQuery.data?.rows ?? [];

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => compareRows(left, right, sortKey, sortDir));
  }, [rows, sortKey, sortDir]);

  function toggleSort(nextKey: SortKey): void {
    if (sortKey === nextKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "name" ? "asc" : "desc");
  }

  function openEditModal(row: EndUserRow): void {
    setEditRow(row);
    setModalRateInput(formatAdjustmentPercentInput(row.commission_adjustment_rate));
    setModalReason(row.commission_adjustment_reason ?? "");
  }

  function saveEditModal(): void {
    if (editRow == null) {
      return;
    }
    const rate = parseAdjustmentPercentInput(modalRateInput);
    const reason = rate == null ? null : modalReason.trim();
    saveMutation.mutate({
      fsAccountId: editRow.fs_account_id,
      commission_adjustment_rate: rate,
      commission_adjustment_reason: reason,
    });
  }

  const hoverHint =
    newEndUserRule?.summary ?? "Loading new-end-user rule from the commissions engine…";

  const modalParsedRate = editRow != null ? parseAdjustmentPercentInput(modalRateInput) : null;
  const modalReasonRequired = modalParsedRate != null;
  const modalReasonTrimmed = modalReason.trim();
  const modalCanSave =
    editRow != null &&
    !saveMutation.isPending &&
    (!modalReasonRequired || modalReasonTrimmed !== "") &&
    (modalParsedRate !== editRow.commission_adjustment_rate ||
      modalReasonTrimmed !== (editRow.commission_adjustment_reason ?? "").trim());

  const columns: HubDataTableColumn<EndUserRow, SortKey>[] = (() => {
    const base: HubDataTableColumn<EndUserRow, SortKey>[] = [
      {
        id: "name",
        header: "Name",
        sortKey: "name",
        overflow: "truncate",
        sortValue: (row) => row.name ?? "",
        cell: (row) => (
          <EndUserDetailLink block fsAccountId={row.fs_account_id} endUserName={row.name} />
        ),
      },
      {
        id: "new_end_user_bonus_starts_on",
        header: "New end user bonus started",
        sortKey: "new_end_user_bonus_starts_on",
        overflow: "nowrap",
        sortValue: (row) => row.new_end_user_bonus_starts_on ?? "",
        cell: (row) => formatDate(row.new_end_user_bonus_starts_on),
      },
      {
        id: "new_end_user_bonus_ends_on",
        header: "New end user bonus ends",
        sortKey: "new_end_user_bonus_ends_on",
        overflow: "nowrap",
        sortValue: (row) => row.new_end_user_bonus_ends_on ?? "",
        cell: (row) => formatDate(row.new_end_user_bonus_ends_on),
      },
      {
        id: "first_sold",
        header: (
          <span className="inline-flex items-center gap-1">
            First sold
            <HoverHintWithGlyph hoverHint={FIRST_SOLD_COLUMN_HOVER_HINT} size="sm" />
          </span>
        ),
        sortKey: "first_sold",
        overflow: "nowrap",
        sortValue: (row) => row.first_sold ?? "",
        cell: (row) => formatDate(row.first_sold),
      },
      {
        id: "date_last_sold",
        header: (
          <span className="inline-flex items-center gap-1">
            Last sold
            <HoverHintWithGlyph hoverHint={LAST_SOLD_COLUMN_HOVER_HINT} size="sm" />
          </span>
        ),
        sortKey: "date_last_sold",
        overflow: "nowrap",
        sortValue: (row) => row.date_last_sold ?? "",
        cell: (row) => formatDate(row.date_last_sold),
      },
      {
        id: "commission_adjustment_rate",
        header: "Adjustment",
        sortKey: "commission_adjustment_rate",
        align: "right",
        overflow: "nowrap",
        sortValue: (row) => row.commission_adjustment_rate ?? "",
        cell: (row) => (
          <span className="inline-flex items-center justify-end gap-1">
            <span>{formatAdjustment(row.commission_adjustment_rate)}</span>
            {row.commission_adjustment_rate != null &&
            row.commission_adjustment_reason != null &&
            row.commission_adjustment_reason.trim() !== "" ? (
              <HoverHintWithGlyph hoverHint={row.commission_adjustment_reason} size="sm" />
            ) : null}
          </span>
        ),
      },
    ];
    if (canEdit) {
      base.push({
        id: "actions",
        header: "",
        sortKey: "name",
        sortValue: () => "",
        cell: (row) => (
          <div className="text-right">
            <Button type="button" variant="ghost" size="sm" onClick={() => openEditModal(row)}>
              {row.commission_adjustment_rate != null ? "Edit" : "Add"}
            </Button>
          </div>
        ),
      });
    }
    return base;
  })();

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={hoverHint}>Commission end users</PageTitleWithHelp>

      {!canEdit ? (
        <p className="text-sm text-muted-foreground">
          View only — commission module administrators can edit per-end-user commission adjustments.
        </p>
      ) : null}

      <HubDataTable
        title={
          <>
            End users <CountLabel rows={rows} />
          </>
        }
        columns={columns}
        rows={sortedRows}
        rowKey={(row) => row.fs_account_id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={listQuery.isPending}
        loadingMessage="Loading end users…"
        error={
          listQuery.isError
            ? listQuery.error instanceof Error
              ? listQuery.error.message
              : "Could not load end users."
            : null
        }
        empty="No end users with sales-order history."
        tableClassName="min-w-[56rem]"
      />

      {saveMutation.isError ? (
        <p className="text-sm text-red-700">
          {saveMutation.error instanceof Error
            ? saveMutation.error.message
            : "Could not save commission adjustment."}
        </p>
      ) : null}

      {editRow != null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(event) => {
            if (event.target === event.currentTarget && !saveMutation.isPending) {
              setEditRow(null);
            }
          }}
        >
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-base font-semibold text-slate-900">Commission adjustment</h2>
              <p className="mt-1 text-sm text-slate-600">{editRow.name}</p>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <Label htmlFor="end-user-adjustment-rate">Adjustment rate (%)</Label>
                <Input
                  id="end-user-adjustment-rate"
                  type="text"
                  className="mt-1"
                  placeholder="e.g. -1.5"
                  value={modalRateInput}
                  onChange={(event) => setModalRateInput(event.target.value)}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Added to the rep base rate for this end user. Leave empty to remove an adjustment.
                </p>
              </div>
              <div>
                <Label htmlFor="end-user-adjustment-reason">
                  Reason{modalReasonRequired ? " (required)" : ""}
                </Label>
                <textarea
                  id="end-user-adjustment-reason"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500"
                  rows={3}
                  value={modalReason}
                  onChange={(event) => setModalReason(event.target.value)}
                  disabled={!modalReasonRequired}
                  placeholder={
                    modalReasonRequired
                      ? "Why this end user has a commission rate adjustment"
                      : "Set an adjustment rate to add a reason"
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditRow(null)}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="button" onClick={saveEditModal} disabled={!modalCanSave}>
                {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
